import React from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ExternalLink, ShoppingCart } from 'lucide-react';

export const StorePage = () => {
  const storeItems = [
    {
      id: 1,
      title: 'You, Me & Her - Digital',
      description: 'Stream the award-winning romantic comedy on your favorite platform.',
      price: 'From $4.99',
      type: 'TVOD',
      image: 'https://images.unsplash.com/photo-1625690303837-654c9666d2d0',
      links: [
        { platform: 'Prime Video', url: 'https://amazon.com/dp/B0DC2KLX7D' },
        { platform: 'Apple TV+', url: 'https://tv.apple.com/us/movie/you-me--her/umc.cmc.4b2kpnqz44p5wcthg0ehmlnbg' },
        { platform: 'Starz', url: 'https://www.starz.com/us/en/movies/you-me-her-73175' }
      ]
    },
    {
      id: 2,
      title: 'Be The Train - Book',
      description: 'The mindset and tools you need to make your first feature film. A powerful guide for unstoppable creators.',
      price: '$18.99',
      type: 'Book',
      image: 'https://images.unsplash.com/photo-1577190651915-bf62d54d5b36',
      links: [
        { platform: 'Amazon', url: 'https://www.amazon.com/Be-Train-Mindset-Tools-Feature/dp/B0DVBYB1KJ' }
      ]
    },
    {
      id: 3,
      title: 'Manifestation Cardigan',
      description: 'Exclusive Manifestation Wear collection. Custom-made statement pieces woven with intention. Limited drop.',
      price: 'Custom Order',
      type: 'Merch',
      image: 'https://images.unsplash.com/photo-1617658946816-d35c3adc3835',
      links: [
        { platform: 'Order Now', url: 'https://bethetrain.com' }
      ]
    },
    {
      id: 4,
      title: 'Space Weed Universe NFT',
      description: 'Create your Martian and join the Space Weed Universe. Limited NFT collection on Fundgible.',
      price: 'TBA',
      type: 'NFT',
      image: 'https://images.unsplash.com/photo-1686797859030-d6d171ddb329',
      links: [
        { platform: 'Fundgible', url: 'https://fundgible.xyz/project/spaceweed' }
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="editorial-container py-12 lg:py-20 border-b border-border">
        <div className="max-w-4xl">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold editorial-serif mb-6 leading-tight">
            Store
          </h1>
          <p className="text-lg lg:text-xl text-muted-foreground leading-relaxed max-w-2xl">
            Support independent creators. Stream films, buy books, collect limited merchandise, 
            and participate in our artist ecosystem.
          </p>
        </div>
      </section>

      {/* Store Grid */}
      <section className="editorial-container py-12 lg:py-16">
        <div className="grid md:grid-cols-2 gap-8 lg:gap-10">
          {storeItems.map((item) => (
            <Card key={item.id} className="overflow-hidden border-0 shadow-none hover:shadow-lg transition-shadow group">
              <div className="editorial-image-hover relative aspect-[4/3]">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover"
                />
                <Badge className="absolute top-4 left-4 bg-primary text-primary-foreground">
                  {item.type}
                </Badge>
              </div>
              <div className="p-6">
                <h3 className="text-2xl lg:text-3xl font-semibold editorial-serif mb-2 group-hover:text-primary transition-colors">
                  {item.title}
                </h3>
                <p className="text-base text-muted-foreground leading-relaxed mb-4">
                  {item.description}
                </p>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xl font-semibold text-foreground">{item.price}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {item.links.map((link, idx) => (
                    <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer" className="flex-1 min-w-[140px]">
                      <Button variant={idx === 0 ? 'default' : 'outline'} className="w-full" size="sm">
                        {link.platform}
                        <ExternalLink size={14} className="ml-2" />
                      </Button>
                    </a>
                  ))}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* Info Section */}
      <section className="editorial-container py-12 lg:py-16 border-t border-border">
        <div className="editorial-narrow text-center">
          <ShoppingCart size={48} className="mx-auto mb-6 text-muted-foreground" />
          <h2 className="text-3xl lg:text-4xl font-bold editorial-serif mb-4">
            More Coming Soon
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed mb-8">
            We're constantly expanding our store with new releases, exclusive merchandise, 
            and unique creator collaborations. Subscribe to stay updated on new drops.
          </p>
          <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Your email address"
              className="flex-1 px-4 py-3 rounded-sm border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <button
              type="submit"
              className="px-6 py-3 bg-primary text-primary-foreground rounded-sm text-sm font-medium hover:opacity-90 transition-opacity"
            >
              Notify Me
            </button>
          </form>
        </div>
      </section>

      {/* Support Section */}
      <section className="editorial-container py-16 lg:py-24 border-t border-border">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl lg:text-4xl font-bold editorial-serif mb-6 text-center">
            Why Shop With Us?
          </h2>
          <div className="grid sm:grid-cols-3 gap-8 text-center">
            <div>
              <h3 className="text-lg font-semibold mb-2">Support Creators</h3>
              <p className="text-sm text-muted-foreground">
                Every purchase directly supports independent artists and filmmakers.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">Exclusive Access</h3>
              <p className="text-sm text-muted-foreground">
                Get early access to new releases and limited edition merchandise.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">Join the Ecosystem</h3>
              <p className="text-sm text-muted-foreground">
                Become part of a community building sustainable creative practices.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default StorePage;